import { useState } from "react";
import axios from "axios";
import { TextField, TextArea, ChoiceField, SelectField, FormSection } from "./FormFields";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const YN = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];
const YNNS = [...YN, { value: "not_sure", label: "Not sure" }];
const LEVELS = ["basic", "conversational", "fluent", "native"].map((v) => ({ value: v, label: v[0].toUpperCase() + v.slice(1) }));
const HEARD = ["instagram", "facebook", "linkedin", "friend", "worldpackers", "other"].map((v) => ({
  value: v,
  label: v === "linkedin" ? "LinkedIn" : v[0].toUpperCase() + v.slice(1),
}));

const EMPTY = {
  full_name: "", email: "", phone: "", based_in: "", right_to_work: "", earliest_start: "",
  english_level: "", spanish_level: "", other_languages: "", salary_expectation: "",
  sober_ok: "", dogs_ok: "", driving_licence: "", experience: "", most_useful_fix: "", links: "",
  sunday_scenario: "", first_month: "", partnership_meaning: "", why_this_why_now: "",
  video_link: "", heard_from: "", website: "",
};

const REQUIRED = ["full_name", "email", "phone", "right_to_work", "salary_expectation", "sober_ok", "sunday_scenario", "why_this_why_now", "video_link"];

const validate = (f) => {
  const e = {};
  REQUIRED.forEach((k) => { if (!f[k].trim()) e[k] = "That one's required."; });
  if (f.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = "That email doesn't look right.";
  if (f.video_link.trim() && !/^https?:\/\/\S+\.\S+/.test(f.video_link.trim())) e.video_link = "That needs to be a link, starting with http.";
  return e;
};

export const ApplicationForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);

  const set = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    if (errors[k]) setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate(form);
    if (Object.keys(e).length) {
      setErrors(e);
      document.querySelector(`[data-testid="field-${Object.keys(e)[0]}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSending(true);
    setFailed(false);
    try {
      await axios.post(`${API}/careers/apply`, form);
      setDone(true);
      window.scrollTo({ top: document.getElementById("apply")?.offsetTop - 80 || 0, behavior: "smooth" });
    } catch {
      setFailed(true);
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="bg-[#F4ECE2] rounded-xl sm:rounded-2xl border border-stone-100 p-6 sm:p-8" data-testid="application-success">
        <p className="text-[#8C9A7C] font-medium text-sm mb-2">Received</p>
        <p className="font-serif text-2xl sm:text-3xl text-[#2E2A26] leading-snug">
          Got it. We read every one of these properly, so give us a few days.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-5" data-testid="application-form">
      <input type="text" name="website" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

      <FormSection title="The basics">
        <TextField n={1} label="Full name" name="full_name" value={form.full_name} onChange={set} required error={errors.full_name} />
        <TextField n={2} label="Email" name="email" type="email" value={form.email} onChange={set} required error={errors.email} />
        <TextField n={3} label="Phone / WhatsApp" name="phone" type="tel" value={form.phone} onChange={set} required error={errors.phone} />
        <TextField n={4} label="Where are you based right now?" name="based_in" value={form.based_in} onChange={set} />
        <ChoiceField n={5} label="Do you have the right to work in Spain?" name="right_to_work" value={form.right_to_work} onChange={set} options={YNNS} required error={errors.right_to_work} />
        <TextField n={6} label="Earliest start date" name="earliest_start" type="date" value={form.earliest_start} onChange={set} />
      </FormSection>

      <FormSection title="Languages">
        <ChoiceField n={7} label="English level" name="english_level" value={form.english_level} onChange={set} options={LEVELS} />
        <ChoiceField n={8} label="Spanish level" name="spanish_level" value={form.spanish_level} onChange={set} options={LEVELS} />
        <TextField n={9} label="Any other languages?" name="other_languages" value={form.other_languages} onChange={set} help="Optional." />
      </FormSection>

      <FormSection title="The money bit">
        <TextField
          n={10}
          label="The role includes your own room and food. With that in mind, what monthly salary are you expecting?"
          name="salary_expectation"
          value={form.salary_expectation}
          onChange={set}
          required
          help="Net, in euros. An honest number helps us both."
          error={errors.salary_expectation}
        />
      </FormSection>

      <FormSection title="Fit">
        <ChoiceField n={11} label="Savi is alcohol and drug free, for staff as well as guests. Are you comfortable living and working under that rule?" name="sober_ok" value={form.sober_ok} onChange={set} options={YN} required error={errors.sober_ok} />
        <ChoiceField n={12} label="We have three resident dogs. Any problem with that?" name="dogs_ok" value={form.dogs_ok} onChange={set} options={YN} />
        <ChoiceField n={13} label="Do you have a driving licence valid in Spain?" name="driving_licence" value={form.driving_licence} onChange={set} options={YN} />
      </FormSection>

      <FormSection title="Experience">
        <TextArea n={14} label="Tell us about any experience running a team, a house, a venue, a community or something similar." name="experience" value={form.experience} onChange={set} />
        <TextArea n={15} label="What's the most useful thing you've ever fixed? Could be a boiler, could be a person's week." name="most_useful_fix" value={form.most_useful_fix} onChange={set} />
        <TextField n={16} label="Link to a CV, LinkedIn, or anything else that shows us who you are" name="links" value={form.links} onChange={set} help="Optional." />
      </FormSection>

      <FormSection title="How you think">
        <TextArea n={17} label="It's Sunday evening, the pool pump has died, a volunteer hasn't turned up for their shift and two guests are quietly at war over the kitchen. What do you do first?" name="sunday_scenario" value={form.sunday_scenario} onChange={set} required error={errors.sunday_scenario} />
        <TextArea n={18} label="You've been here a month. What's one thing you'd want to have started?" name="first_month" value={form.first_month} onChange={set} rows={4} />
        <TextArea n={19} label="The role can lead to a partnership in the Savi brand. What would that mean to you?" name="partnership_meaning" value={form.partnership_meaning} onChange={set} rows={4} />
      </FormSection>

      <FormSection title="Finally">
        <TextArea n={20} label="Why this, and why now?" name="why_this_why_now" value={form.why_this_why_now} onChange={set} required help="Keep it short. We'll notice." rows={4} error={errors.why_this_why_now} />
        <TextField n={21} label="Send us a 60-second video introducing yourself" name="video_link" value={form.video_link} onChange={set} required help="Paste a link. Unlisted YouTube, Google Drive, WeTransfer or Loom all work. Phone footage is fine." placeholder="https://" error={errors.video_link} />
        <SelectField n={22} label="How did you hear about us?" name="heard_from" value={form.heard_from} onChange={set} options={HEARD} />
      </FormSection>

      <div className="border-t border-[#2E2A26]/20 pt-6 flex flex-col gap-3 items-start">
        <button
          type="submit"
          disabled={sending}
          className="bg-[#C85A38] hover:bg-[#B04E30] disabled:opacity-60 text-[#FBF7F1] font-sans text-[15px] px-8 py-3 rounded-[2px] transition-colors duration-150"
          data-testid="application-submit"
        >
          {sending ? "Sending" : "Send it"}
        </button>
        {Object.keys(errors).some((k) => errors[k]) && (
          <p className="font-label text-[11px] text-[#C85A38]" data-testid="application-errors">A few bits are missing. They're marked above.</p>
        )}
        {failed && (
          <p className="font-label text-[11px] text-[#C85A38]" data-testid="application-failed">
            Something went wrong at our end. Try again in a minute, or email hola@savicoliving.com.
          </p>
        )}
        <p className="font-label text-[11px] text-[#2E2A26]/50 leading-relaxed max-w-xl">
          We'll use what you send us to consider your application and nothing else. It stays with Steve and Marina.
        </p>
      </div>
    </form>
  );
};
